import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect } from 'react';

import { RootContainerProvider } from '#/context/RootContainerContext';
import { useBootMeasure } from '#/hooks/usePerformanceMeasure';
import { pushPageView } from '#/lib/gtm';
import { initInteractionTracking } from '#/lib/interaction-tracking';
import { ROUTER_BASE_PATH } from '#/lib/router-base-path';

import { routeTree } from './routeTree.gen';

const router = createRouter({ routeTree, basepath: ROUTER_BASE_PATH });

// Track SPA navigations for GTM. Each resolved navigation pushes a page-view to
// the dataLayer. pushPageView appends to window.dataLayer regardless of whether
// GTM was initialized, so this is safe even when GTM is disabled for an
// environment (the events are simply never consumed by a container).
router.subscribe('onResolved', ({ toLocation }) => {
  pushPageView(toLocation.pathname);
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Root application component.
 *
 * @remarks
 *
 * Should contain all of the mandatory wrappers (e.g. providers) surrounding
 * the primary router entrypoint.
 */
export function App({ rootContainer }: { rootContainer?: ShadowRoot }) {
  useBootMeasure();

  // Delegated interaction tracking (clicks, link clicks, tooltip views). Scoped
  // to the shadow root when embedded so composedPath() yields the true inner
  // element; falls back to the host document in the standalone build. The effect
  // returns the teardown fn so listeners are removed under StrictMode's dev
  // double-mount.
  useEffect(() => {
    const target = rootContainer ?? document;
    const getPagePath = () => router.state.location.pathname;
    return initInteractionTracking(target, getPagePath);
  }, [rootContainer]);

  return (
    <StrictMode>
      <RootContainerProvider value={rootContainer ?? null}>
        <RouterProvider router={router} />
      </RootContainerProvider>
    </StrictMode>
  );
}
